/**
 * Typed wrappers for Tauri invoke commands.
 * Every Tauri command gets a typed TypeScript function.
 */

import { invoke } from "@tauri-apps/api/core";

// --- Types matching Rust command return types ---

/** Lightweight world info returned from world management commands. */
export interface WorldInfo {
  id: string;
  name: string;
  seed: string;
  updated_at: string;
}

/** A single GURPS skill check result. */
export interface SkillCheck {
  skill_name: string;
  effective_level: number;
  roll: number;
  margin: number;
  success: boolean;
  critical: boolean;
}

/** Narrative context for LLM agents. */
export interface NarrativeContext {
  action: string;
  character_name: string;
  location: string;
  skill_results: string[];
  time_description: string;
  events: string[];
  social_notes: string[];
  recent_history: string[];
  narrative_directive?: string;
  active_arcs?: string;
  immediate_memory?: string;
  recent_memory?: string;
  deep_memory?: string;
}

/** Story arc info from the engine. */
export interface StoryArcInfo {
  id: string;
  title: string;
  status: string;
  visibility: string;
  description: string;
  stakes: string;
  tension: number;
  turns_since_update: number;
}

/** Narrative memory state from the engine. */
export interface NarrativeMemoryInfo {
  immediate_count: number;
  recent_count: number;
  deep_count: number;
  immediate_text: string;
  recent_text: string;
  deep_text: string;
}

/** Result of processing a player action through the engine. */
export interface TurnResult {
  action: string;
  skill_checks: SkillCheck[];
  time_elapsed: string;
  events: string[];
  social_effects: string[];
  narrative_context: NarrativeContext;
  turn: number;
}

/** Current world state summary. */
export interface WorldState {
  world_id: string;
  world_name: string;
  character_name: string;
  time: string;
  turn: number;
  rules_mode: string;
  recent_actions?: string[];
  last_narrative?: string;
  setting_prompt?: string;
  location?: string;
}

/** Info about a stored API key (masked). */
export interface KeyInfo {
  provider: string;
  masked: string;
  is_set: boolean;
}

/** Parameters for creating a world with full setting customization. */
export interface WorldCreationParams {
  name: string;
  seed: string;
  settingPrompt: string;
  genre: string;
  era: string;
  tone: string;
}

/** Character creation data sent to Rust for validation and storage. */
export interface CharacterCreationData {
  name: string;
  st: number;
  dx: number;
  iq: number;
  ht: number;
  skills: Record<string, number>;
  background: string;
  archetype: string;
  rationale: string;
}

// --- World commands ---

/** Create a new world with the given name and seed. */
export function createWorld(name: string, seed: string): Promise<WorldInfo> {
  return invoke<WorldInfo>("create_world", { name, seed });
}

/** Create a new world with full setting customization. */
export function createWorldWithSetting(params: WorldCreationParams): Promise<WorldInfo> {
  return invoke<WorldInfo>("create_world_with_setting", {
    name: params.name,
    seed: params.seed,
    settingPrompt: params.settingPrompt,
    genre: params.genre,
    era: params.era,
    tone: params.tone,
  });
}

/** Replace the default character with an LLM-assigned one. */
export function updateCharacter(data: CharacterCreationData): Promise<void> {
  return invoke<void>("update_character", { data });
}

/** Get the current character sheet as JSON string. */
export function getCharacter(): Promise<string> {
  return invoke<string>("get_character");
}

/** Get the unmasked raw API key for a provider. */
export function getRawKey(provider: string): Promise<string | null> {
  return invoke<string | null>("get_raw_key", { provider });
}

/** List all available worlds. */
export function listWorlds(): Promise<WorldInfo[]> {
  return invoke<WorldInfo[]>("list_worlds");
}

/** Delete a world by its ID. */
export function deleteWorld(worldId: string): Promise<void> {
  return invoke<void>("delete_world", { worldId });
}

/** Faction reputation info. */
export interface FactionRepInfo {
  name: string;
  rep: number;
}

/** Timeline entry from the event graph. */
export interface TimelineEntry {
  time: string;
  text: string;
}

/** Journal entry from the event graph. */
export interface JournalEntryInfo {
  entry_type: string;
  title: string;
  body: string;
}

/** Set the current location description. */
export function setLocation(location: string): Promise<void> {
  return invoke<void>("set_location", { location });
}

/** Get all faction reputations for the active world. */
export function getFactionReps(): Promise<FactionRepInfo[]> {
  return invoke<FactionRepInfo[]>("get_faction_reps");
}

/** Get the event timeline for the active world. */
export function getTimeline(): Promise<TimelineEntry[]> {
  return invoke<TimelineEntry[]>("get_timeline");
}

/** Get journal entries for the active world. */
export function getJournal(): Promise<JournalEntryInfo[]> {
  return invoke<JournalEntryInfo[]>("get_journal");
}

// --- Narrative system commands ---

/** Record a narrative beat extracted by the LLM. */
export function recordBeat(beatJson: string): Promise<void> {
  return invoke<void>("record_beat", { beatJson });
}

/** Get all story arcs for the active world. */
export function getStoryArcs(): Promise<StoryArcInfo[]> {
  return invoke<StoryArcInfo[]>("get_story_arcs");
}

/** Get narrative memory state for the active world. */
export function getNarrativeMemory(): Promise<NarrativeMemoryInfo> {
  return invoke<NarrativeMemoryInfo>("get_narrative_memory");
}

/** Data for adding a story arc to the engine. */
export interface StoryArcData {
  id: string;
  title: string;
  description: string;
  stakes: string;
  initial_visibility: string;
  initial_tension: number;
  decay_behavior: string;
  related_locations: string[];
  advancement_triggers: string[];
}

/** Add a story arc to the active world. */
export function addStoryArc(arc: StoryArcData): Promise<void> {
  return invoke<void>("add_story_arc", { arc });
}

/** Data for recording a detected pact. */
export interface PactData {
  pattern: string;
  target: string;
  turn: number;
}

/** Record a pact detected from beat consequences. */
export function createPact(pact: PactData): Promise<void> {
  return invoke<void>("create_pact", { pact });
}

// --- Turn commands ---

/** Process a player action and return the turn result. */
export function processAction(action: string, skill?: string): Promise<TurnResult> {
  return invoke<TurnResult>("process_action", { action, skill: skill ?? null });
}

/** Get the current world state summary. */
export function getWorldState(): Promise<WorldState> {
  return invoke<WorldState>("get_world_state");
}

/** Load an existing world by ID. */
export function loadWorld(worldId: string): Promise<WorldInfo> {
  return invoke<WorldInfo>("load_world", { worldId });
}

/** Persist the last narrative text for resume. */
export function saveNarrative(narrative: string): Promise<void> {
  return invoke<void>("save_narrative", { narrative });
}

// --- Key commands ---

/** Store an API key for a provider. */
export function storeApiKey(provider: string, key: string): Promise<void> {
  return invoke<void>("store_api_key", { provider, key });
}

/** Get info about a stored API key (masked). */
export function getApiKey(provider: string): Promise<KeyInfo> {
  return invoke<KeyInfo>("get_api_key", { provider });
}

/** Delete an API key for a provider. */
export function deleteApiKey(provider: string): Promise<void> {
  return invoke<void>("delete_api_key", { provider });
}

/** Validate an API key by making a test call to the provider. */
export function validateApiKey(provider: string, key: string): Promise<boolean> {
  return invoke<boolean>("validate_api_key", { provider, key });
}
